import React, { useState } from 'react';
import { Modal, Form, Input, message, Card, Avatar, Space, Typography, Divider, Button } from 'antd';
import { CommentOutlined, SendOutlined, CloseOutlined, UserOutlined } from '@ant-design/icons';
import { useReturnWithComment } from '../../hooks/memo/memoQuery';

const { TextArea } = Input;
const { Title, Text } = Typography;

export default function ReturnCommentModal({ visible, onClose, memo, onSuccess }) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const returnWithComment = useReturnWithComment();

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const payload = {
                memo: memo.id,
                content: values.content
            };

            const response = await returnWithComment(payload);
            message.success('Comment submitted and memo returned successfully');
            form.resetFields();
            onSuccess && onSuccess(response);
            onClose();
        } catch (error) {
            console.error('Error returning memo with comment:', error);
            message.error('Failed to submit comment');
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    return (
        <Modal
            open={visible}
            onCancel={handleCancel}
            footer={null}
            width={650}
            centered
            closable={false}
            className="modern-modal"
            styles={{
                body: { padding: 0 },
                header: { display: 'none' }
            }}
        >
            <div style={{ 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                padding: '24px',
                borderRadius: '8px 8px 0 0'
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Avatar 
                            size={40} 
                            style={{ 
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                color: 'white'
                            }}
                            icon={<CommentOutlined />}
                        />
                        <div>
                            <Title level={4} style={{ color: 'white', margin: 0 }}>
                                Return with Comment
                            </Title>
                            <Text style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '14px' }}>
                                Provide your feedback and return the memo
                            </Text>
                        </div>
                    </div>
                    <Button
                        type="text"
                        icon={<CloseOutlined />}
                        onClick={handleCancel}
                        style={{ 
                            color: 'white',
                            border: 'none',
                            background: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '50%',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    />
                </div>
            </div>

            <div style={{ padding: '24px' }}>
                <Card 
                    style={{ 
                        marginBottom: '24px',
                        border: '1px solid #f0f0f0',
                        borderRadius: '8px',
                        background: '#fafafa'
                    }}
                    bodyStyle={{ padding: '16px' }}
                >
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                        <Text strong style={{ color: '#1890ff', fontSize: '16px' }}>
                            {memo?.title}
                        </Text>
                        {memo?.commentRequestedByUser && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Avatar size={20} icon={<UserOutlined />} />
                                <Text type="secondary" style={{ fontSize: '14px' }}>
                                    <strong>Requested by:</strong> {memo.commentRequestedByUser.name}
                                </Text>
                            </div>
                        )}
                        <Text type="secondary" style={{ fontSize: '14px' }}>
                            Please provide your detailed comment or recommendation on this memo
                        </Text>
                    </Space>
                </Card>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    style={{ marginBottom: '24px' }}
                >
                    <Form.Item
                        name="content"
                        label={
                            <Text strong style={{ fontSize: '16px' }}>
                                <CommentOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                                Your Comment/Recommendation
                            </Text>
                        }
                        rules={[
                            { required: true, message: 'Please provide your comment' },
                            { min: 10, message: 'Comment must be at least 10 characters long' }
                        ]}
                    >
                        <TextArea
                            rows={6}
                            placeholder="Provide your detailed comment or recommendation..."
                            maxLength={500}
                            showCount
                            style={{ 
                                borderRadius: '8px',
                                fontSize: '14px'
                            }}
                        />
                    </Form.Item>
                </Form>

                <Divider style={{ margin: '24px 0' }} />

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <Button 
                        size="large" 
                        onClick={handleCancel}
                        style={{ 
                            borderRadius: '8px',
                            height: '44px',
                            paddingLeft: '24px',
                            paddingRight: '24px'
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        type="primary" 
                        size="large"
                        loading={loading}
                        onClick={() => form.submit()}
                        icon={<SendOutlined />}
                        style={{ 
                            borderRadius: '8px',
                            height: '44px',
                            paddingLeft: '24px',
                            paddingRight: '24px',
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            border: 'none'
                        }}
                    >
                        Submit Comment
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
